// Entry point for build
import React from 'react'
import { createRoot } from 'react-dom/client'
import type { Root } from 'react-dom/client'
import type { FindMyPlanConfig } from './types/config'
import type { FMPProps } from './lib/types'
import { FMP } from './lib/find-my-plan'

// Preflight-free embed styles for the host document head. The full stylesheet
// (with preflight) is injected inside the Shadow DOM by ShadowDomWrapper, so
// this must not reset partner-page elements. See ENERGYDXP-2218.
import '@strata-monorepo/shared-ui/styles/globals-embed.css'

const roots: Record<string, Root> = {}

/**
 * Initialize the FindMyPlan component in the specified container
 */
const init = async (containerId: string, props: FMPProps = {}): Promise<void> => {
  try {
    const container = document.getElementById(containerId)
    if (container) {
      // If a root already exists for this container, unmount it before creating a new one
      if (roots[containerId]) {
        roots[containerId].unmount()

        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete roots[containerId]
      }

      const root = createRoot(container)
      roots[containerId] = root
      root.render(
        <React.StrictMode>
          <FMP {...props} />
        </React.StrictMode>,
      )
    } else {
      console.error(`FindMyPlan Error: Container with id #${containerId} not found.`)
    }
  } catch (error) {
    console.error('[FindMyPlan] Initialization failed:', error)
  }
}

/**
 * Unmount the FindMyPlan component from the specified container
 */
const unmount = (containerId: string): void => {
  const root = roots[containerId]
  if (root) {
    root.unmount()

    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete roots[containerId]
  }
}

/**
 * FindMyPlan class for script-based initialization
 * Mirrors the Strata library pattern for consistent usage across the monorepo
 */
export class FindMyPlan {
  private readonly config: FindMyPlanConfig

  /**
   * Initialize FindMyPlan with the provided configuration
   * @param config - Configuration object containing target element ID and optional props
   */
  constructor(config: FindMyPlanConfig) {
    this.config = config
    this.initialize().catch((error) => {
      console.error('[FindMyPlan] Initialization failed:', error)
    })
  }

  /**
   * Initialize the component by mounting it to the target element
   */
  private async initialize(): Promise<void> {
    const { targetElementId, fmpProps = {} } = this.config
    await init(targetElementId, fmpProps)
  }

  /**
   * Destroy the component and clean up resources
   */
  public destroy(): void {
    unmount(this.config.targetElementId)
  }
}

// Expose FindMyPlan globally for script usage
declare global {
  interface Window {
    FindMyPlan: typeof FindMyPlan
  }
}

window.FindMyPlan = FindMyPlan
